import {
  Input,
  Label,
  MaskedInput,
  RichTextEditor,
  Select,
  SelectOption,
  TextArea,
  ToggleButton
} from '@pay-com/components'
import { TaskDetail, TaskField, TaskFieldType } from 'generated/graphql'
import { ChangeEvent } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { yesNoOptions } from 'consts'
import { TaskDocumentsField } from '../TaskDocumentsField'
import '@pay-com/components/lib/style.css'

type TaskGenericInputProps = {
  field: TaskField
  taskDetails?: TaskDetail
  defaultValue?: string
  isReadonly?: boolean
}

export const TaskGenericInput = ({
  field,
  taskDetails,
  defaultValue,
  isReadonly
}: TaskGenericInputProps) => {
  const { control } = useFormContext()

  switch (field.type) {
    case TaskFieldType.AutoComplete:
    case TaskFieldType.Select:
      return (
        <Controller
          name={field.id}
          control={control}
          render={({ field: formField }) => {
            const options = field.options ?? []
            const rawValue =
              (formField.value as string | undefined) ?? defaultValue
            const currentValue =
              options.find(option => option.value === rawValue) ?? null

            return (
              <Select
                isSearchable={field.type === TaskFieldType.AutoComplete}
                searchByLabelAndByValue
                label={field.label}
                placeholder={field.placeholder ?? ''}
                value={currentValue}
                readonly={isReadonly}
                options={options}
                onChange={(option: SelectOption | null) =>
                  formField.onChange(option?.value ?? null)
                }
              />
            )
          }}
        />
      )
    case TaskFieldType.Boolean:
      return (
        <Controller
          name={field.id}
          control={control}
          render={({ field: formField }) => (
            <>
              <Label label={field.label} disabled={isReadonly} />
              <ToggleButton
                className="max-w-96"
                options={yesNoOptions}
                defaultValue={
                  defaultValue != null ? defaultValue === 'true' : undefined
                }
                readOnly={isReadonly}
                onClick={toggleValue => formField.onChange(toggleValue)}
              />
            </>
          )}
        />
      )
    case TaskFieldType.Date:
      return (
        <Controller
          name={field.id}
          control={control}
          render={({ field: formField }) => (
            <MaskedInput
              label={field.label}
              format="##/##/####"
              placeholder="DD/MM/YYYY"
              mask="_"
              onChange={evt => formField.onChange(evt.target.value)}
              readOnly={isReadonly}
            />
          )}
        />
      )
    case TaskFieldType.Documents:
      return (
        <Controller
          name={field.id}
          control={control}
          render={({ field: formField, fieldState }) => (
            <TaskDocumentsField
              field={field}
              taskDetails={taskDetails}
              value={formField.value}
              onChange={val => formField.onChange(val)}
              error={fieldState.error?.message}
            />
          )}
        />
      )
    case TaskFieldType.MultiSelect:
      return (
        <Controller
          name={field.id}
          control={control}
          render={({ field: formField }) => (
            <Select
              isMulti
              label={field.label}
              placeholder={field.placeholder ?? ''}
              defaultValue={field.options?.filter(option =>
                defaultValue?.includes(option.value)
              )}
              searchByLabelAndByValue
              showSearchFieldInsideDropdown
              options={field.options ?? []}
              readonly={isReadonly}
              onChange={options =>
                formField.onChange(
                  options?.map((e: SelectOption) => e.value) || []
                )
              }
            />
          )}
        />
      )
    case TaskFieldType.TextArea:
      return (
        <Controller
          name={field.id}
          control={control}
          render={({ field: formField }) => (
            <TextArea
              key={field.id}
              label={field.label}
              placeholder={field.placeholder || ''}
              defaultValue={defaultValue}
              readOnly={isReadonly}
              onChange={(evt: ChangeEvent<HTMLTextAreaElement>) =>
                formField.onChange(evt.target.value)
              }
            />
          )}
        />
      )
    case TaskFieldType.RichText:
      return (
        <Controller
          name={field.id}
          control={control}
          render={({ field: formField }) => (
            <div>
              <Label
                label={field.label}
                disabled={isReadonly}
                className="mb-1.5"
              />
              <RichTextEditor
                key={field.id}
                placeholder={field.placeholder || ''}
                readOnly={isReadonly}
                markdown={((formField.value as string) ?? defaultValue) || ''}
                onChange={markdown => formField.onChange(markdown)}
              />
            </div>
          )}
        />
      )
    case TaskFieldType.Email:
    case TaskFieldType.Number:
    case TaskFieldType.StringifiedNumber:
    case TaskFieldType.Text:
    default:
      return (
        <Controller
          name={field.id}
          control={control}
          render={({ field: formField }) => (
            <Input
              key={field.id}
              label={field.label}
              placeholder={field.placeholder || ''}
              defaultValue={defaultValue}
              readOnly={isReadonly}
              onChange={evt => formField.onChange(evt.target.value)}
            />
          )}
        />
      )
  }
}
